import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaPlaneDeparture,
  FaShieldAlt,
  FaBroom,
  FaBuilding,
  FaHardHat,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';

import '../styling/Agencies.css';

import travelImg from '../images/travel.jpg';
import securityImg from '../images/security.jpg';
import cleaningImg from '../images/cleaning.jpg';
import rentalImg from '../images/rental.jpg';
import constructionImg from '../images/construction.jpg';

const agencyGroups = [
  {
    category: "Agencies",
    agencies: [
      {
        title: "Travel Agency",
        description: "All your travel needs, handled professionally.",
        icon: <FaPlaneDeparture />,
        img: travelImg,
        link: "/travel",
      },
      {
        title: "Security Agency",
        description: "Professional protection services.",
        icon: <FaShieldAlt />,
        img: securityImg,
        link: "/security",
      },
      {
        title: "Cleaning Agency",
        description: "Reliable and professional cleaning.",
        icon: <FaBroom />,
        img: cleaningImg,
        link: "/cleaning",
      },
      {
        title: "Real Estate Agency",
        description: "Find your next home or investment.",
        icon: <FaBuilding />,
        img: rentalImg,
        link: "/realestate",
      },
      {
        title: "Construction Agency",
        description: "From concept to completion — we build your vision.",
        icon: <FaHardHat />,
        img: constructionImg,
        link: "/construction",
      },
    ],
  },
];

function Agencies() {
  const scrollRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const cardWidth = 300; // Approximate width + gap of each card

  // Update scroll buttons visibility and index
  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(container.scrollLeft < maxScrollLeft - 5);
    setCurrentIndex(Math.round(container.scrollLeft / cardWidth));
  };

  // Slide container left or right by one card
  const slide = (direction) => {
    const container = scrollRef.current;
    if (!container) return;
    let newScrollLeft;
    if (direction === 'left') {
      newScrollLeft = Math.max(container.scrollLeft - cardWidth, 0);
    } else {
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      newScrollLeft = Math.min(container.scrollLeft + cardWidth, maxScrollLeft);
    }
    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  // Scroll directly to a card by index
  const handleScrollTo = (index) => {
    const container = scrollRef.current;
    if (container) {
      container.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth',
      });
      setCurrentIndex(index);
    }
  };

  useEffect(() => {
    handleScroll();
    // Recalculate on window resize for better UX
    window.addEventListener('resize', handleScroll);
    return () => window.removeEventListener('resize', handleScroll);
  }, []);

  return (
    <div className="agencies-container">


      {/* Agencies horizontal slider */}
      <section className="agencies">
        <h2>Our Agencies</h2>
        {agencyGroups.map(({ category, agencies }) => (
          <div
            key={category}
            className={`agency-group ${canScrollRight ? 'scrollable-right' : ''}`}
          >
            {/* Left arrow */}
            {canScrollLeft && (
              <button
                className="scroll-arrow left"
                onClick={() => slide('left')}
                aria-label="Scroll left"
              >
                <FaChevronLeft />
              </button>
            )}

            <div
              className="agency-list horizontal-scroll"
              ref={scrollRef}
              onScroll={handleScroll}
            >
              {agencies.map(({ title, description, icon, img, link }, idx) => (
                <Link to={link} key={idx} className="agency-item">
                  <img src={img} alt={`${title} agency`} />
                  <div className="agency-content">
                    <h4><span className="icon">{icon}</span> {title}</h4>
                    <p>{description}</p>
                    <p className="read-more">Read More</p>
                  </div>
                </Link>
              ))}
            </div>

            {/* Right arrow */}
            {canScrollRight && (
              <button
                className="scroll-arrow right"
                onClick={() => slide('right')}
                aria-label="Scroll right"
              >
                <FaChevronRight />
              </button>
            )}

            {/* Scroll indicators */}
            <div className="scroll-indicators">
              {agencies.map((_, idx) => (
                <span
                  key={idx}
                  className={`dot ${idx === currentIndex ? 'active' : ''}`}
                  onClick={() => handleScrollTo(idx)}
                />
              ))}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

export default Agencies;
